import { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { TABLE, BUTTONS, PLACEHOLDERS, FORM, ROLES, USER } from 'locales/locales'
import { GridColumn } from '@progress/kendo-react-grid'
import KGrid from 'components/kendo/grid/KGrid'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import { icons } from 'utils/icons'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import { Checkbox } from '@progress/kendo-react-inputs'
import { FixMeLater } from 'types'
import {
  useLazyGetRoleExternalUsersQuery,
  useLazyGetRoleUsersQuery,
} from 'app/services/system.service'
import CustomText from 'components/text/CustomText'

interface User {
  _id: string
  fullname: string
  avatar: string
  isInRole?: boolean
}

interface Role {
  _id: string
  name: string
}

interface UserRoleManagementProps {
  selectedRole: Role | null
  selectedUsers: User[]
  roleExternalUsers: User[]
  onUserInRoleChange: (user: User, checked: boolean) => void
  onBatchUserRoleChange: (users: User[], checked: boolean) => void
  setExternalUsers: (users: User[]) => void
  setSelectedUsers: (users: User[]) => void
}

const UserRoleManagement = ({
  selectedRole,
  selectedUsers,
  roleExternalUsers,
  onUserInRoleChange,
  onBatchUserRoleChange,
  setExternalUsers,
  setSelectedUsers,
}: UserRoleManagementProps) => {
  const { t } = useTranslation()

  const [lazyGetRoleExternalUsers] = useLazyGetRoleExternalUsersQuery()
  const [lazyGetRoleUsers] = useLazyGetRoleUsersQuery()
  // Users panel states
  const [usersLoading, setUsersLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [dataState, setDataState] = useState({
    take: 10,
    skip: 0,
    filter: {
      logic: 'and',
      filters: [] as FixMeLater[],
    },
    sort: [],
  })
  const [userFilter, setUserFilter] = useState<FixMeLater>(null)
  const [searchValue, setSearchValue] = useState('')

  // Dropdown options for user filtering
  const userFilterOptions = useMemo(
    () => [
      {
        title: t(ROLES.IN_ROLE),
        value: 'inRole',
      },
      {
        title: t(ROLES.NOT_IN_ROLE),
        value: 'notInRole',
      },
    ],
    [],
  )

  // Initialize user filter
  useEffect(() => {
    if (!userFilter && userFilterOptions?.length) {
      setUserFilter(userFilterOptions[0])
    }
  }, [userFilter, userFilterOptions,])

  useEffect(() => {
    if (selectedRole) {
      setUserFilter(userFilterOptions[0])
      setSearchValue('')
      setDataState({
        ...dataState,
        filter: {
          logic: 'and',
          filters: [],
        },
        skip: 0,
      })
    }
  }, [selectedRole])

  // Users panel filter and search handlers
  const handleUsersFilterChange = useCallback(
    (event: FixMeLater) => {
      setDataState({
        ...dataState,
        filter: event.filter,
      })
    },
    [dataState],
  )

  const handleUsersDataStateChange = useCallback(
    (event: FixMeLater) => {
      setDataState({
        ...dataState,
        skip: event.dataState.skip,
        take: event.dataState.take,
        sort: event.dataState.sort || [],
      })
    },
    [dataState],
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value)
    setDataState({
      ...dataState,
      filter: {
        logic: 'or',
        filters: value ? [
          { field: 'fullname', operator: 'contains', value: value as FixMeLater },
          { field: 'email', operator: 'contains', value: value as FixMeLater },
        ] : [],
      },
      skip: 0,
    })
  }, [dataState])


  const fetchExternalUsers = useCallback(async () => {
    if (!selectedRole) return 0
    try {
      setUsersLoading(true)
      const filter = {
        ...dataState,
        count: true,
        id: selectedRole?._id,
      }
      const response = await lazyGetRoleExternalUsers(filter)
      const totalCount = response.data.total || 0
      setExternalUsers(
        response.data.data.map((user: FixMeLater) => ({
          ...user,
          isInRole: false,
        })) || [],
      )
      return totalCount
    } catch (error) {
      console.error('Error fetching external users:', error)
      return 0
    } finally {
      setUsersLoading(false)
    }
  }, [selectedRole, dataState, lazyGetRoleExternalUsers])

  const fetchRoleUsers = useCallback(async () => {
    if (!selectedRole) return 0
    try {
      setUsersLoading(true)
      const filter = {
        ...dataState,
        count: true,
        id: selectedRole?._id,
      }
      const response = await lazyGetRoleUsers(filter)
      const totalCount = response.data.total || 0
      setSelectedUsers(
        response.data.data.map((user: FixMeLater) => ({
          ...user,
          isInRole: true,
        })) || [],
      )
      return totalCount
    } catch (error) {
      console.error('Error fetching role users:', error)
      return 0
    } finally {
      setUsersLoading(false)
    }
  }, [selectedRole, dataState, lazyGetRoleUsers])

  const handleUserFilterDropdownChange = useCallback(
    (e: FixMeLater) => {
      const newFilter = userFilterOptions.find((option) => option.value === e.value)
      setUserFilter(newFilter)
      
      // Reset search and pagination when changing filter
      setSearchValue('')
      setDataState({
        ...dataState,
        filter: {
          logic: 'and',
          filters: [],
        },
        skip: 0,
      })

      // Clear current data first
      setSelectedUsers([])
      setExternalUsers([])
    },
    [userFilterOptions, dataState],
  )

  // Initial data load when role or filter changes
  useEffect(() => {
    if (selectedRole && userFilter) {
      if (userFilter.value === 'inRole') {
        setTotal(0)
        fetchRoleUsers().then((total) => setTotal(total || 0))
      } else if (userFilter.value === 'notInRole') {
        setTotal(0)
        fetchExternalUsers().then((total) => setTotal(total || 0))
      }
    }
  }, [selectedRole, userFilter, fetchExternalUsers, fetchRoleUsers])

  // Data refresh when search or pagination changes
  useEffect(() => {
    if (selectedRole && userFilter && (dataState.filter.filters.length > 0 || dataState.skip > 0)) {
      if (userFilter.value === 'inRole') {
        setTotal(0)
        fetchRoleUsers().then((total) => setTotal(total || 0))
      } else if (userFilter.value === 'notInRole') {
        setTotal(0)
        fetchExternalUsers().then((total) => setTotal(total || 0))
      }
    }
  }, [dataState.filter, dataState.skip, dataState.take, dataState.sort, selectedRole, userFilter, fetchExternalUsers, fetchRoleUsers])

  const handleUsersReloadClick = useCallback(async () => {
    if (selectedRole && userFilter) {
      if (userFilter.value === 'inRole') {
        setTotal(0)
        const total = await fetchRoleUsers()
        setTotal(total || 0)
      } else if (userFilter.value === 'notInRole') {
        setTotal(0)
        const total = await fetchExternalUsers()
        setTotal(total || 0)
      }
    }
  }, [selectedRole, userFilter, fetchExternalUsers, fetchRoleUsers])

  // Custom cells
  const CustomCellForAvatar = (props: FixMeLater) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: props.color,
        }}
      >
        <Avatar
          type={undefined}
          source={props?.dataItem?.avatar || ""}
          text={props?.dataItem?.fullname || "--"}
          size={AvatarSize.average}
        />
      </td>
    )
  }

  const avatarCustomCell = (props: FixMeLater) => <CustomCellForAvatar {...props} color={'red'} />

  const checkBoxCustomCell = (props: FixMeLater) => {
    const handleDivClick = () => {
      onUserInRoleChange(props.dataItem, !props.dataItem.isInRole)
    }

    return (
      <div
        {...props.tdProps}
        onClick={handleDivClick}
        style={{
          color: props.color,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          height: '56px',
          cursor: 'pointer',
        }}
        colSpan={1}
      >
        <StyledCheckbox
          checked={props.dataItem.isInRole}
          onChange={(e) => onUserInRoleChange(props.dataItem, e.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )
  }

  const checkBoxCustomHeaderCell = (props: FixMeLater) => {
    const currentUsers = userFilter?.value === 'inRole' ? selectedUsers : roleExternalUsers
    const allUsersSelected = currentUsers.length > 0 && currentUsers.every(user => user.isInRole === true)
    const handleSelectAll = (checked: boolean) => {
      const usersToUpdate = currentUsers.filter(user => user.isInRole !== checked)
      if (usersToUpdate.length === 0) return
      onBatchUserRoleChange(usersToUpdate, checked)
    }

    return (
      <div
        style={{
          color: props.color,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StyledCheckbox checked={allUsersSelected} onChange={(e) => handleSelectAll(e.value)} />
      </div>
    )
  }


  return (
    <UsersPanel>
      <TitleContainer>
        <TitleLeft>
          <StyledCustomText typography="header1">{t(BUTTONS.USERS)}</StyledCustomText>
        </TitleLeft>
      </TitleContainer>
      {selectedRole && (
        <FilterContainer>
          <FilterLeft>
            <FilterGroup>
              <StyledCustomText typography="header3">{t(FORM.FILTER)}:</StyledCustomText>
              <Dropdown
                style={{ width: '140px', fontSize: '11px' }}
                options={userFilterOptions}
                id="user-filter-dropdown"
                selectedOption={userFilter}
                onChangeOption={handleUserFilterDropdownChange}
              />
            </FilterGroup>
          </FilterLeft>
          <FilterRight>
            <UserSearchInput
              placeholder={t(PLACEHOLDERS.USER_SEARCH_PLACEHOLDER)}
              value={searchValue}
              onChange={handleSearchChange}
            />
            <ReloadButton onClick={handleUsersReloadClick}>
              {usersLoading ? (
                <LoadingSpinner />
              ) : (
                <img
                  className="refresh"
                  height={15}
                  src={icons.refreshIcon.src}
                  srcSet={icons.refreshIcon.srcset}
                />
              )}
            </ReloadButton>
          </FilterRight>
        </FilterContainer>
      )}
      <PanelContent hasFilter={!!selectedRole}>
        {selectedRole ? (
          <GridContainer>
            <KGrid
              loading={usersLoading}
              datasource={userFilter?.value === 'inRole' ? selectedUsers : roleExternalUsers}
              dataState={dataState}
              pageable={true}
              total={total}
              filterable={false}
              editMode={false}
              sortable={true}
              onFilterChange={handleUsersFilterChange}
              onDataStateChange={handleUsersDataStateChange}
              style={{ height: '100%' }}
            >
              <GridColumn
                field="isInRole"
                filterable={false}
                sortable={false}
                title="Rolde Mi"
                headerCell={checkBoxCustomHeaderCell}
                cell={checkBoxCustomCell}
              />
              <GridColumn
                field="avatar"
                filterable={false}
                sortable={false}
                title={t(USER.AVATAR)}
                cell={avatarCustomCell}
              />
              <GridColumn field="fullname" title={t(TABLE.USER_FULLNAME)} />
            </KGrid>
          </GridContainer>
        ) : (
          <EmptyMessage>Lütfen bir rol seçin</EmptyMessage>
        )}
      </PanelContent>
    </UsersPanel>
  )
}

export default UserRoleManagement

// Styled Components
const StyledCustomText = styled(CustomText)`
  word-wrap: normal;
  white-space: nowrap;
  @media (max-width: 1024px) {
    font-size: 12px;
  }
`

const UsersPanel = styled.div`
  border: 1px solid #e3ebf6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
`

const TitleContainer = styled.div`
  padding: 10px 14px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 8px;
    padding: 16px;
  }
`

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
`

const FilterContainer = styled.div`
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 16px;
  }
`

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1024px) {
    justify-content: space-between;
    margin-bottom: 8px;
  }
`

const FilterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 1024px) {
    flex: none;
    width: 100%;
  }
`



const PanelContent = styled.div<{ hasFilter?: boolean }>`
  height: calc(100% - ${props => props.hasFilter ? '96px' : '48px'});
  overflow: auto;
`

const GridContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .k-grid {
    border: 1px solid #e0e0e0;

    /*     .k-grid-header {
      height: 30px !important;
      border: 1px solid #129fdb !important;
      .k-table-th {
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 30px !important;
        border: 1px solid #129fdb !important;
      }
    } */

    .k-grid-header {
      position: sticky;
      top: 0;
      z-index: 2;
      background-color: white;
      .k-header {
        height: 30px !important;
      }
    }

    .k-grid-content {
      flex: 1;
      overflow-y: auto;
    }

    .k-pager-wrap {
      position: sticky;
      bottom: 0;
      z-index: 2;
      background-color: white;
      border-top: 1px solid #e0e0e0;
    }
  }
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const UserSearchInput = styled.input`
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  height: 30px;
  border-radius: 4px;
  background-color: white;
  width: 130px;
  font-size: 11px;
  &::placeholder {
    color: #888;
  }
  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
`



const ReloadButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const EmptyMessage = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  margin-top: 50px;
`

const StyledCheckbox = styled(Checkbox)`
  .k-checkbox {
    margin: 0;
    border: 1px solid #424040;
  }
`